// ---------------------------------------------------------------------------
// UI Block Registry Bootstrap — imports shared definitions and wires Konva
// renderer functions to them.  Called once at app startup.
//
// This file lives in ui/ (can import Konva).  The definitions live in
// server/shared/ (cannot import Konva).  This is the bridge.
// ---------------------------------------------------------------------------

import { blockRegistry } from "./shared/block-registry/index.js";
// Ensure shared init has run (registers all definitions)
import "./shared/block-registry/init.js";

// ── Import VizDsl renderers ─────────────────────────────────────────────
import { renderSectionHeader } from "./renderers/section_header.js";
import { renderLegend } from "./renderers/legend.js";
import { renderTextBlock } from "./renderers/text_block.js";
import { renderConditional } from "./renderers/conditional.js";
import { renderTileRow } from "./renderers/tile_row.js";
import { renderBulletList } from "./renderers/bullet_list.js";
import { renderChart } from "./renderers/chart.js";
import { renderGantt } from "./renderers/gantt.js";
import { renderMap } from "./renderers/map.js";
import { renderFlow } from "./VizDslLayoutEngine.js";
import { renderWriteBox } from "./renderers/write_box.js";
import { renderCriteriaList } from "./renderers/criteria_list.js";
import { renderImage } from "./renderers/image.js";

// ── Wire renderer functions to definitions ──────────────────────────────

// Phase B: Simple blocks
(function wirePhaseB() {
  // section_header
  {
    const def = blockRegistry.get("section_header");
    def.renderer = {
      render: renderSectionHeader,
      layout: { isContentSized: true, canStretch: false },
      debugColor: "rgba(128,128,128,0.03)",
    };
  }

  // legend
  {
    const def = blockRegistry.get("legend");
    def.renderer = {
      render: renderLegend,
      layout: { isContentSized: true, canStretch: false },
      debugColor: "rgba(128,128,128,0.03)",
    };
  }

  // heading, paragraph, list, table, section — DocDsl renderers
  // are class methods on DocDslPageRenderer.  They will be wired when
  // DocDslRenderer.js is refactored to use the registry (Phase F).
  // For now, they render via the existing switch statement.
  {
    const def = blockRegistry.get("image");
    def.renderer = {
      render: renderImage,
      layout: { isContentSized: false, canStretch: true },
      debugColor: "rgba(80,120,180,0.04)",
    };
  }
})();

// Phase C: Data blocks
(function wirePhaseC() {
  {
    const def = blockRegistry.get("text_block");
    def.renderer = {
      render: renderTextBlock,
      layout: { isContentSized: true, canStretch: true },
      debugColor: "rgba(100,149,237,0.04)",
    };
  }

  {
    const def = blockRegistry.get("conditional");
    def.renderer = {
      render: renderConditional,
      layout: { isContentSized: false, canStretch: true },
      debugColor: "rgba(186,85,211,0.04)",
    };
  }

  {
    const def = blockRegistry.get("tile_row");
    def.renderer = {
      render: renderTileRow,
      layout: { isContentSized: true, canStretch: true },
      debugColor: "rgba(255,165,0,0.04)",
    };
  }
})();

// Phase D: Complex blocks
(function wirePhaseD() {
  {
    const def = blockRegistry.get("bullet_list");
    def.renderer = {
      render: renderBulletList,
      layout: { isContentSized: true, canStretch: false },
      debugColor: "rgba(220,20,60,0.04)",
    };
  }

  {
    const def = blockRegistry.get("chart");
    def.renderer = {
      render: renderChart,
      // Container-sized by default; heatmap + progress are content-sized
      layout: { isContentSized: false, canStretch: true },
      debugColor: "rgba(60,179,113,0.04)",
    };
  }

  {
    const def = blockRegistry.get("map");
    def.renderer = {
      render: renderMap,
      layout: { isContentSized: false, canStretch: true },
      debugColor: "rgba(186,85,211,0.04)",
    };
  }

  {
    const def = blockRegistry.get("gantt");
    def.renderer = {
      render: renderGantt,
      // Natural height depends on group/lane count, not the allocated box.
      layout: { isContentSized: true, canStretch: false },
      debugColor: "rgba(8,166,232,0.04)",
    };
  }
})();

// Phase E: Layout engine
(function wirePhaseE() {
  {
    const def = blockRegistry.get("flow");
    def.renderer = {
      render: renderFlow,
      layout: { isContentSized: false, canStretch: true },
      debugColor: "rgba(128,128,128,0.03)",
    };
  }
})();

// Phase F: Static layout blocks
(function wirePhaseF() {
  {
    const def = blockRegistry.get("write_box");
    def.renderer = {
      render: renderWriteBox,
      layout: { isContentSized: false, canStretch: true },
      debugColor: "rgba(100,200,100,0.03)",
    };
  }
  {
    const def = blockRegistry.get("criteria_list");
    def.renderer = {
      render: renderCriteriaList,
      layout: { isContentSized: false, canStretch: true },
      debugColor: "rgba(200,150,50,0.03)",
    };
  }
})();

export { blockRegistry };
